/**
 * errorCopy - the operator-facing error copy registry (WS-9 Core wave 1).
 *
 * Every operator-visible failure answers four questions, in the
 * operator's language (build-specs/error-surfacing.md):
 *
 *   1. What happened        - plain words, no codes on screen.
 *   2. Is my work safe      - said out loud whenever it is TRUE.
 *   3. What do I do now     - the action that actually works.
 *   4. Who fixes it if that fails - self-serve vs. escalate, and where.
 *
 * The severity class drives the fourth answer:
 *
 *   self_heals     the system already retried or will resume; usually
 *                  never shown (absorbed by the auto-retry).
 *   self_serve     the operator's next action resolves it; the action
 *                  IS the fix, no escalation line.
 *   retriable      transient; retry later plausibly works; the action
 *                  ends ", and if it happens again, tell <system contact>".
 *   needs_admin    config or permission; the action itself is the ask
 *                  ("Ask <contact> to <specific thing>").
 *   needs_platform a bug or infra failure; nothing on-site fixes it.
 *
 * <contact> is tenant config, never hardcoded: the
 * `tenants.config.support.contacts.{system,billing,admin}` seam (LP-30),
 * each {name, channel} and each falling back to `support.contact`, read by
 * readSupportContact with the generic code fallback below (the
 * readRateBands defensive-walk shape). Commercial tenants set their own.
 * A sentence names the contact for what it is about: {contact_system},
 * {contact_billing} or {contact_admin}.
 *
 * Copy rules (house, LP-30): two short sentences at most, one concrete
 * step, no word a nurse would need explained, Olive never talks about
 * itself, no "like" for examples, never "please", no
 * "audit/log/void" in staff-facing copy, no blame, no drama, no em
 * dashes. Meanings match the per-form strings this registry replaced;
 * this file is taxonomy + tone, not semantics.
 */

import { ApiError, authExpiredWorkKept, isAuthExpiredError, isDatabaseBusy } from './api';
import { PhotoUnreadableError } from './photoIntakeErrors';
import { endSentence } from './sentence';

// ---------------------------------------------------------------------
// The support-contact config seam
// ---------------------------------------------------------------------

/** One person to tell: who, and where to reach them. */
export interface ContactPerson {
  /** Who to tell ("Amanda", "the office"); generic words when unset. */
  name: string;
  /** Where to reach them ("amanda@...", "#help"); null when unset. */
  channel: string | null;
}

/** What a sentence is about decides who it names (LP-30): `system` for
 *  Olive problems (a crash, a page that cannot be reached, a fault that
 *  is not the person's), `billing` for invoices, rates, QuickBooks and
 *  payroll hours, `admin` for access, people, the Directory and who owns
 *  a client. */
export type ContactCategory = 'system' | 'billing' | 'admin';
export const CONTACT_CATEGORIES: readonly ContactCategory[] = ['system', 'billing', 'admin'];

/** The tenant's support contact (`support.contact`), carrying the
 *  per-category contacts (`support.contacts.<category>`) when read from
 *  config. A category that is absent reads as this contact. */
export interface SupportContact extends ContactPerson {
  system?: ContactPerson;
  billing?: ContactPerson;
  admin?: ContactPerson;
}

/** The code fallback when `config.support.contact` is unset: the
 *  practice's admin in generic words (no tenant-specific name to fall
 *  back to in product code). */
export const FALLBACK_SUPPORT_CONTACT: SupportContact = {
  name: 'your administrator',
  channel: null,
};

/** One `{name, channel}` object, or null when it is not one: a contact
 *  needs a non-empty name; channel is optional. */
function readContactPerson(value: unknown): ContactPerson | null {
  if (typeof value !== 'object' || value === null) return null;
  const name = (value as Record<string, unknown>).name;
  if (typeof name !== 'string' || name.trim().length === 0) return null;
  const channel = (value as Record<string, unknown>).channel;
  return {
    name: name.trim(),
    channel: typeof channel === 'string' && channel.trim().length > 0 ? channel.trim() : null,
  };
}

/**
 * Read `config.support.contact` ({name, channel}) and the three
 * `config.support.contacts.{system,billing,admin}` from the tenants.config
 * blob (the readRateBands defensive-walk shape). Each category falls back
 * to `support.contact`, and that to FALLBACK_SUPPORT_CONTACT, when unset
 * or malformed. Pure.
 */
export function readSupportContact(config: unknown): SupportContact {
  const support =
    typeof config === 'object' && config !== null ? (config as Record<string, unknown>).support : undefined;
  const supportObj = typeof support === 'object' && support !== null ? (support as Record<string, unknown>) : {};
  const general = readContactPerson(supportObj.contact) ?? FALLBACK_SUPPORT_CONTACT;
  const contacts =
    typeof supportObj.contacts === 'object' && supportObj.contacts !== null
      ? (supportObj.contacts as Record<string, unknown>)
      : {};
  const generalPerson: ContactPerson = { name: general.name, channel: general.channel };
  return {
    ...generalPerson,
    system: readContactPerson(contacts.system) ?? generalPerson,
    billing: readContactPerson(contacts.billing) ?? generalPerson,
    admin: readContactPerson(contacts.admin) ?? generalPerson,
  };
}

/** The person a sentence about `category` names. */
export function contactFor(contact: SupportContact, category: ContactCategory): ContactPerson {
  return contact[category] ?? contact;
}

/** "Amanda (amanda@peace.care)" when a channel is set; just the name
 *  otherwise. This is what `{contact_<category>}` renders as in the copy. */
export function contactDisplay(contact: ContactPerson): string {
  return contact.channel === null ? contact.name : `${contact.name} (${contact.channel})`;
}

// ---------------------------------------------------------------------
// The taxonomy
// ---------------------------------------------------------------------

export type ErrorSeverity =
  | 'self_heals'
  | 'self_serve'
  | 'retriable'
  | 'needs_admin'
  | 'needs_platform';

/**
 * One failure in two sentences (LP-30): `what` and `workSafe` join into
 * the first ("That didn't save, and nothing was changed."), `action` and
 * the escalation into the second ("Try again, and if it happens again,
 * tell ..."). Each part is written without its closing period.
 */
export interface ErrorDescriptor {
  /** Answer 1: what happened, in plain words. May use {vars}. */
  what: string;
  /** Answer 2: the work-safety clause, joined to `what` with a comma
   *  ("and nothing was changed"); present whenever it is TRUE. */
  workSafe?: string;
  /** Answer 3: the action that actually works. needs_admin actions
   *  name the contact via {contact_<category>}. */
  action: string;
  /** Drives answer 4: see the class table in the header. */
  severity: ErrorSeverity;
  /** The action finishes the sentence `what` begins, joined with a comma:
   *  one sentence where the owner wrote one. */
  oneSentence?: true;
}

/** The honest generic for codes the registry does not know: something
 *  broke, and no on-site action fixes it. */
export const UNKNOWN_FAILURE: ErrorDescriptor = {
  what: 'Something went wrong here',
  action: 'Tell {contact_system} what you were doing and when',
  severity: 'needs_platform',
};

/** Answer 4 for a retriable fault, the clause its action ends with; null
 *  for every other class (the action is the fix, or already names who to
 *  tell). */
export function escalationOf(severity: ErrorSeverity, contact: SupportContact): string | null {
  return severity === 'retriable'
    ? `, and if it happens again, tell ${contactDisplay(contactFor(contact, 'system'))}`
    : null;
}

// ---------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------

export type ErrorSurface =
  | 'email_generate'
  | 'email_open'
  | 'document_download'
  | 'document_pdf'
  | 'document_print'
  | 'face_sheet'
  | 'assessment_generate'
  | 'assessment_letter_generate'
  | 'brand_format_generate'
  | 'med_list_generate'
  | 'med_list_photo_generate'
  | 'med_list_pdf_generate'
  | 'photo_intake_extract'
  | 'insurance_card_extract'
  | 'email_refine'
  | 'assessment_refine'
  | 'email_draft_save'
  | 'assessment_draft_save'
  | 'brand_draft_save'
  | 'med_list_draft_save'
  | 'assessment_file'
  | 'note_post'
  | 'note_read'
  | 'note_draft_save'
  | 'note_summary_save'
  | 'note_mark_ready'
  | 'note_remove'
  | 'page_read'
  | 'page_write'
  | 'page_crash'
  | 'not_found'
  | 'billing_household';

/** The entries every AI generate surface shares (429 / 403 / 502 tail);
 *  the per-tool nouns and 400/413 shapes are supplied by each surface.
 *  `inputSafeLine` is the clause the first sentence ends with. */
function aiGenerateShared(inputSafeLine: string): Record<string, ErrorDescriptor> {
  return {
    rate_limited: {
      what: 'AI Tools are busy right now',
      workSafe: inputSafeLine,
      action: 'Wait a minute, then press Generate again',
      severity: 'self_serve',
    },
    cap_reached: {
      what: "You've used your AI allowance for now",
      workSafe: inputSafeLine,
      action: 'Try again tomorrow, or ask {contact_admin} to raise your limit',
      severity: 'needs_admin',
    },
    tenant_budget: {
      what: "Your organization has used today's AI allowance",
      workSafe: inputSafeLine,
      action: 'Try again tomorrow, or ask {contact_admin} to raise it',
      severity: 'needs_admin',
    },
    no_access: {
      what: "AI Tools aren't turned on for you yet",
      action: 'Ask {contact_admin} to turn them on',
      severity: 'needs_admin',
    },
    invalid_shape: {
      what: 'The answer came back garbled',
      workSafe: inputSafeLine,
      action: 'Press Generate again',
      severity: 'retriable',
    },
    service_down: {
      what: "AI Tools didn't answer",
      workSafe: inputSafeLine,
      action: 'Try again in a few minutes',
      severity: 'retriable',
    },
  };
}

function draftSaveShared(capAction: string): Record<string, ErrorDescriptor> {
  return {
    cap_reached: {
      what: 'Your drafts inbox is full ({limit} drafts)',
      workSafe: 'but this draft stays on screen',
      action: capAction,
      severity: 'self_serve',
    },
    conflict: {
      what: "That draft didn't save",
      workSafe: "but it's still on screen",
      action: 'Save again',
      severity: 'retriable',
    },
    no_access: {
      what: "Saving drafts isn't turned on for you yet",
      action: 'Ask {contact_admin} to turn it on',
      severity: 'needs_admin',
    },
    invalid_payload: {
      what: "This draft came out in a form that won't save",
      workSafe: "but it's still on screen",
      action: 'Press Generate again, then save',
      severity: 'self_serve',
    },
  };
}

function refineShared(unchangedLine: string): Record<string, ErrorDescriptor> {
  return {
    rate_limited: {
      what: 'AI Tools are busy right now',
      workSafe: unchangedLine,
      action: 'Wait a minute, then ask for your change again',
      severity: 'self_serve',
    },
    cap_reached: {
      what: "You've used your AI allowance for now",
      workSafe: unchangedLine,
      action: 'Try again tomorrow, or ask {contact_admin} to raise your limit',
      severity: 'needs_admin',
    },
    tenant_budget: {
      what: "Your organization has used today's AI allowance",
      workSafe: unchangedLine,
      action: 'Try again tomorrow, or ask {contact_admin} to raise it',
      severity: 'needs_admin',
    },
    no_access: {
      what: "Asking for changes isn't turned on for you yet",
      action: 'Ask {contact_admin} to turn it on',
      severity: 'needs_admin',
    },
    failed: {
      what: "Your change didn't finish",
      workSafe: unchangedLine,
      action: 'Try again',
      severity: 'retriable',
    },
  };
}

const PHOTOS_OFF: ErrorDescriptor = {
  what: "Reading photos isn't turned on for your organization",
  action: 'Ask {contact_admin} to turn it on',
  severity: 'needs_admin',
};

/** A text past the AI tools' input ceiling (src/lib/aiInputLimits.ts),
 *  said under the box before any call and again when the server refuses
 *  one with 400 text_too_long. Which sentence a box reads is its kind in
 *  src/lib/aiTextTooLong.ts, in the owner's words: a pasted
 *  document is run in halves (text_too_long), a short box says how many
 *  characters to remove (text_too_long_shorten, {over_chars} counted from
 *  the box), "Who is this for?" says only names go there
 *  (text_too_long_recipient), and a text the refine carries from the
 *  original draft says to draft again (text_too_long_carried).
 *  text_too_long_shorten_uncounted is the short box's sentence when the
 *  count cannot be taken. {max_chars} and {over_chars} are numbers as the
 *  form wrote them ("20,000"); {box} is the form's label, never a wire
 *  name. No work-safe line: the box keeps everything pasted, and a line
 *  between the two halves would split the sentence. */
const TEXT_TOO_LONG: ErrorDescriptor = {
  what: 'This takes up to {max_chars} characters at a time',
  action: 'Run the first half, then the second',
  severity: 'self_serve',
};
const TEXT_TOO_LONG_SHORTEN: ErrorDescriptor = {
  what: 'Remove {over_chars} characters to continue',
  action: 'This box takes up to {max_chars}',
  severity: 'self_serve',
};
const TEXT_TOO_LONG_SHORTEN_UNCOUNTED: ErrorDescriptor = {
  what: 'Up to {max_chars} characters.',
  action: 'Shorten it.',
  severity: 'self_serve',
};
const TEXT_TOO_LONG_RECIPIENT: ErrorDescriptor = {
  what: 'Just the names go here, up to {max_chars} characters',
  action: 'Put anything else about them in Email Content',
  severity: 'self_serve',
};
/** The owner's words for a carried text, one per refine surface: one
 *  sentence, its two halves joined with a comma. */
const TEXT_TOO_LONG_CARRIED_EMAIL: ErrorDescriptor = {
  what: 'To change this email',
  action: 'draft it again with {max_chars} characters or fewer in "{box}"',
  severity: 'self_serve',
  oneSentence: true,
};
const TEXT_TOO_LONG_CARRIED_ASSESSMENT: ErrorDescriptor = {
  what: 'To change this assessment',
  action: 'draft it again with notes of {max_chars} characters or fewer',
  severity: 'self_serve',
  oneSentence: true,
};

export const ERROR_COPY: Record<ErrorSurface, Record<string, ErrorDescriptor>> = {
  document_download: {
    failed: {
      what: "Couldn't download here",
      action: 'Open this page in Safari to download',
      severity: 'self_serve',
    },
  },
  document_pdf: {
    failed: {
      what: "The PDF didn't come out",
      workSafe: 'but Download Word still works',
      action: 'Try again, or download Word and save it as a PDF there',
      severity: 'self_serve',
    },
  },
  document_print: {
    failed: {
      what: "This page won't print from here",
      workSafe: 'but Download Word still works',
      action: 'Try again, or download Word and print from there',
      severity: 'self_serve',
    },
    module_stale: {
      what: 'This page is out of date',
      action: 'Reload the page, then press Print or PDF again',
      severity: 'self_serve',
    },
  },
  face_sheet: {
    module_stale: {
      what: 'This page is out of date',
      action: 'Reload the page, then press Download PDF again',
      severity: 'self_serve',
    },
  },
  email_open: {
    copy_failed: {
      what: "Your email didn't copy",
      workSafe: 'but your draft is unchanged',
      action: 'Select the text in the draft, copy it, and paste it into a new email',
      severity: 'self_serve',
    },
  },
  email_generate: {
    ...aiGenerateShared('and nothing you typed is lost'),
    invalid_input: {
      what: 'Something is missing from the form',
      action: 'Check the recipient and topic, then press Generate again',
      severity: 'self_serve',
    },
    text_too_long: TEXT_TOO_LONG,
    text_too_long_shorten: TEXT_TOO_LONG_SHORTEN,
    text_too_long_shorten_uncounted: TEXT_TOO_LONG_SHORTEN_UNCOUNTED,
    text_too_long_recipient: TEXT_TOO_LONG_RECIPIENT,
    // The route's 1 MB body cap; without its own line it would read as
    // "something went wrong on our side".
    too_large: {
      what: 'That text is too large to take in one pass',
      workSafe: 'and nothing you typed is lost',
      action: 'Trim it down and try again',
      severity: 'self_serve',
    },
    max_tokens: {
      what: 'The email ran too long to finish',
      workSafe: 'but everything you entered is still here',
      action: 'Shorten the topic, or split it into two emails',
      severity: 'self_serve',
    },
  },
  assessment_generate: {
    ...aiGenerateShared('but your notes are still here'),
    invalid_input: {
      what: 'Something is missing from the form',
      action: 'Check the recipient, client name and notes, then press Generate again',
      severity: 'self_serve',
    },
    max_tokens: {
      what: 'The report ran too long to finish',
      workSafe: 'but your notes are still here',
      action: 'Split the notes in two and generate each half',
      severity: 'self_serve',
    },
  },
  assessment_letter_generate: {
    ...aiGenerateShared('but your notes are still here'),
    invalid_input: {
      what: 'Something is missing from the form',
      action: 'Check the recipients, client name, RE line and notes, then press Generate again',
      severity: 'self_serve',
    },
    max_tokens: {
      what: 'The letter ran too long to finish',
      workSafe: 'but your notes are still here',
      action: 'Shorten the notes or pick fewer recommendations, then generate again',
      severity: 'self_serve',
    },
  },
  brand_format_generate: {
    ...aiGenerateShared('but your text is still here'),
    invalid_input: {
      what: 'There is no text to format yet',
      action: 'Paste some text first',
      severity: 'self_serve',
    },
    text_too_long: TEXT_TOO_LONG,
    text_too_long_shorten: TEXT_TOO_LONG_SHORTEN,
    text_too_long_shorten_uncounted: TEXT_TOO_LONG_SHORTEN_UNCOUNTED,
    too_large: {
      what: 'That text is too long to format at once',
      workSafe: "but it's still here",
      action: 'Shorten it, then format it again',
      severity: 'self_serve',
    },
    max_tokens: {
      what: 'That document is too long to format at once',
      workSafe: 'but your text is still here',
      action: 'Split it in two and format each part',
      severity: 'self_serve',
    },
  },
  med_list_generate: {
    ...aiGenerateShared('but your list is still here'),
    invalid_input: {
      what: 'There is no list to organize yet',
      action: 'Paste a medication list first',
      severity: 'self_serve',
    },
    text_too_long: TEXT_TOO_LONG,
    too_large: {
      what: 'That list is too long to organize at once',
      workSafe: "but it's still here",
      action: 'Split it in two and organize each part',
      severity: 'self_serve',
    },
    max_tokens: {
      what: 'That list ran too long to organize at once',
      workSafe: "but it's still here",
      action: 'Split it in two and organize each part',
      severity: 'self_serve',
    },
  },
  med_list_pdf_generate: {
    ...aiGenerateShared('and your medication list is unchanged'),
    invalid_input: {
      what: "That PDF couldn't be read",
      workSafe: 'and your medication list is unchanged',
      action: 'Try the PDF again, or take a photo of the list',
      severity: 'self_serve',
    },
    too_large: {
      what: 'That PDF is too big to read here',
      workSafe: 'and your medication list is unchanged',
      action: 'Take a photo or screenshot of the page and use From a photo',
      severity: 'self_serve',
    },
    pdf_too_large: {
      what: 'That PDF is over the 700 KB limit',
      workSafe: 'and your medication list is unchanged',
      action: 'Take a photo or screenshot of the page and use From a photo',
      severity: 'self_serve',
    },
    pdf_not_a_pdf: {
      what: "That file isn't a PDF",
      workSafe: 'and your medication list is unchanged',
      action: 'Choose a PDF, or take a photo of the list',
      severity: 'self_serve',
    },
    pdf_too_many_pages: {
      what: 'That PDF has more than 20 pages',
      workSafe: 'and your medication list is unchanged',
      action: 'Use a PDF of just the medication list',
      severity: 'self_serve',
    },
  },
  med_list_photo_generate: {
    ...aiGenerateShared('and your medication list is unchanged'),
    no_access: PHOTOS_OFF,
    invalid_input: {
      what: "That photo couldn't be read",
      workSafe: 'and your medication list is unchanged',
      action: 'Take a clearer photo in good light, then try it again',
      severity: 'self_serve',
    },
    too_large: {
      what: 'That photo was too big to read',
      workSafe: 'and your medication list is unchanged',
      action: 'Add it again, and it will be shrunk for you',
      severity: 'self_serve',
    },
    max_tokens: {
      what: 'That list is too long to read from one photo',
      workSafe: 'and your medication list is unchanged',
      action: 'Paste the list in instead',
      severity: 'self_serve',
    },
  },
  photo_intake_extract: {
    ...aiGenerateShared('and nothing was added to the record'),
    no_access: PHOTOS_OFF,
    invalid_input: {
      what: "That photo couldn't be read",
      workSafe: 'and nothing was added to the record',
      action: 'Take a clearer photo in good light, then try it again',
      severity: 'self_serve',
    },
    too_large: {
      what: 'That photo was too big to read',
      workSafe: 'and nothing was added to the record',
      action: 'Add it again, and it will be shrunk for you',
      severity: 'self_serve',
    },
    max_tokens: {
      what: 'That list is too long to read from one photo',
      workSafe: 'and nothing was added',
      action: 'Photograph one page at a time and add each one',
      severity: 'self_serve',
    },
  },
  insurance_card_extract: {
    ...aiGenerateShared('and the boxes are unchanged'),
    no_access: PHOTOS_OFF,
    invalid_input: {
      what: "That photo couldn't be read",
      workSafe: 'and the boxes are unchanged',
      action: 'Take a clearer photo of the card in good light, then read it again',
      severity: 'self_serve',
    },
    too_large: {
      what: 'Those photos are too big to read together',
      workSafe: 'and the boxes are unchanged',
      action: 'Read the front on its own, or take the photos closer to the card',
      severity: 'self_serve',
    },
    max_tokens: {
      what: "That card couldn't be read",
      workSafe: 'and the boxes are unchanged',
      action: 'Read the card again',
      severity: 'retriable',
    },
    document_unavailable: {
      what: "That document didn't open",
      workSafe: 'so nothing changed',
      action: 'Try again in a moment',
      severity: 'retriable',
    },
    not_filed: {
      what: 'The insurance was added, but the card photos were not saved to Documents',
      action: 'Add them from the Documents tab',
      severity: 'self_serve',
    },
  },
  // No split box reaches a refine: the editor and Want changes? are
  // shortened, and the texts it carries say to draft again.
  email_refine: {
    ...refineShared('and your draft is unchanged'),
    text_too_long_shorten: TEXT_TOO_LONG_SHORTEN,
    text_too_long_shorten_uncounted: TEXT_TOO_LONG_SHORTEN_UNCOUNTED,
    text_too_long_carried: TEXT_TOO_LONG_CARRIED_EMAIL,
  },
  assessment_refine: {
    ...refineShared('and your report is unchanged'),
    text_too_long_shorten: TEXT_TOO_LONG_SHORTEN,
    text_too_long_shorten_uncounted: TEXT_TOO_LONG_SHORTEN_UNCOUNTED,
    text_too_long_carried: TEXT_TOO_LONG_CARRIED_ASSESSMENT,
  },
  email_draft_save: draftSaveShared('Delete a draft from the drafts inbox, then save again'),
  assessment_draft_save: draftSaveShared('Archive or finalize a draft, then save again'),
  brand_draft_save: draftSaveShared('Delete or finalize a draft, then save again'),
  med_list_draft_save: draftSaveShared('Delete a medication list from the drafts inbox, then save again'),
  assessment_file: {
    no_access: {
      what: "Filing to the client record isn't turned on for you yet",
      action: 'Ask {contact_admin} to turn it on',
      severity: 'needs_admin',
    },
    cap_reached: {
      what: 'This client already has {limit} drafts, the most allowed',
      workSafe: 'but your report is still here',
      action: 'Post or discard one on their record, then file again',
      severity: 'self_serve',
    },
    conflict: {
      what: "That didn't file",
      workSafe: 'but your report is unchanged',
      action: 'Try again',
      severity: 'retriable',
    },
    failed: {
      what: "That didn't file",
      workSafe: 'but your report is unchanged',
      action: 'Try again',
      severity: 'retriable',
    },
  },
  note_post: {
    failed: {
      what: "Your note didn't post",
      workSafe: "but it's back in the box as you wrote it",
      action: 'Post it again',
      severity: 'retriable',
    },
  },
  note_read: {
    read_failed: {
      what: "Your note posted, but it couldn't be read for updates",
      action: 'Press Try again on the note below',
      severity: 'self_serve',
    },
    cut_short: {
      what: 'The reading of this note stopped partway',
      action: 'Press Read this note now beside it',
      severity: 'self_serve',
    },
    not_read: {
      what: "This note couldn't be read for updates",
      action: 'Press Try again beside it, and if it fails again, tell {contact_system}',
      severity: 'self_serve',
    },
    read_cap: {
      what: "Your note posted, but it wasn't read for updates because you've reached your reading limit",
      action: 'Press Read this note now on it later, or ask {contact_admin} to raise your limit',
      severity: 'needs_admin',
    },
    tenant_budget: {
      what: "Your note posted, but it wasn't read for updates because today's AI allowance is used up",
      action: 'Press Read this note now on it tomorrow, or ask {contact_admin} to raise the allowance',
      severity: 'needs_admin',
    },
  },
  note_draft_save: {
    failed: {
      what: "Your draft didn't save",
      workSafe: 'but your note is still in the box',
      action: 'Try again in a moment',
      severity: 'retriable',
    },
  },
  note_summary_save: {
    failed: {
      what: "Your invoice summary edit didn't save",
      workSafe: 'and the note itself is untouched',
      action: 'Try again in a moment',
      severity: 'retriable',
    },
  },
  note_mark_ready: {
    rate_not_on_file: {
      what: 'This client has no rates yet',
      workSafe: "so this note can't go on an invoice",
      action: 'Ask {contact_billing} to add their rates, then mark it Ready',
      severity: 'needs_admin',
    },
    rate_band_missing: {
      what: 'This note needs a rate band',
      workSafe: "so it isn't on the invoice yet",
      action: 'Pick its band, then mark it Ready',
      severity: 'self_serve',
    },
    hours_not_entered: {
      what: 'This note needs its hours',
      workSafe: "so it isn't on the invoice yet",
      action: 'Add the hours, then mark it Ready',
      severity: 'self_serve',
    },
    needs_hours: {
      what: 'This visit has a flat price but zero hours',
      workSafe: "so it isn't on the invoice yet",
      action: 'Add the time you spent, then mark it Ready',
      severity: 'self_serve',
    },
    failed: {
      what: "That didn't mark it Ready",
      workSafe: "so it isn't on the invoice yet",
      action: 'Try again in a moment',
      severity: 'retriable',
    },
  },
  note_remove: {
    failed: {
      what: "That note wasn't removed",
      workSafe: "and it's still on the record",
      action: 'Try again in a moment',
      severity: 'retriable',
    },
  },
  // page_read / page_write: the shared load and save sentences for the
  // screens that have no surface of their own (WS-9 wave 2's per-form
  // registries). {what} is the thing on that screen ("the notes"); {ref}
  // is the reference clause when the server gave one.
  page_read: {
    server: {
      what: "{what} didn't open",
      workSafe: 'and everything you saved is fine',
      action: 'Reload the page, and if it happens again, tell {contact_system}{ref}',
      severity: 'needs_admin',
    },
    busy: {
      what: 'Things are busy for a moment',
      workSafe: 'and everything you saved is fine',
      action: 'Reload in a minute',
      severity: 'retriable',
    },
    offline: {
      what: "You're offline",
      workSafe: 'and everything you saved is fine',
      action: 'Reconnect to the internet, then reload the page',
      severity: 'self_serve',
    },
    unreachable: {
      what: "This page can't be reached right now",
      workSafe: 'and everything you saved is fine',
      action: "Reload in a minute, and if it's still down, tell {contact_system}",
      severity: 'needs_admin',
    },
    not_on_care_team: {
      what: "You're not on this client's care team",
      action: 'Ask {contact_admin} to add you',
      severity: 'needs_admin',
    },
    forbidden: {
      what: "This isn't part of your role",
      action: 'Ask {contact_admin} if it should be',
      severity: 'needs_admin',
    },
    not_found: {
      what: "{what} can't be found",
      workSafe: 'and nothing of yours is affected',
      action: 'Go back and pick it from the list again',
      severity: 'self_serve',
    },
    session: {
      what: 'You were signed out',
      workSafe: 'and everything you saved is still here',
      action: 'Sign in again to carry on',
      severity: 'self_serve',
    },
  },
  billing_household: {
    client_bills_with: {
      what: "This client shares another client's invoice",
      workSafe: 'so no invoice of its own was made',
      action: "Open their Billing details to see whose invoice they're on, then compile that client",
      severity: 'self_serve',
    },
    billing_household_changed: {
      what: 'Someone changed who this client shares an invoice with while you were saving',
      workSafe: 'so nothing changed',
      action: 'Reload the page, then make your change again',
      severity: 'self_serve',
    },
    billing_household_invalid: {
      what: "The people on this shared invoice aren't all linked to one person who gets the bill",
      workSafe: 'so nothing changed',
      action: "Open each person's Billing details and link everyone to that one person",
      severity: 'self_serve',
    },
    bills_with_self: {
      what: "A client can't share an invoice with themselves",
      workSafe: 'so nothing changed',
      action: 'Choose the other person in the couple',
      severity: 'self_serve',
    },
    bills_with_target_is_partner: {
      what: "That person already shares someone else's invoice",
      workSafe: 'so nothing changed',
      action: 'Choose the person whose invoice the couple shares',
      severity: 'self_serve',
    },
    bills_with_client_is_payer: {
      what: "Someone already shares this client's invoice, so this client stays the one who gets the bill",
      action: 'Link the other person to this client instead',
      severity: 'self_serve',
    },
    bills_with_partner_live_invoice: {
      what: "This client has an invoice that isn't sent yet",
      workSafe: 'so nothing changed',
      action: 'Send or discard that invoice first, then combine them',
      severity: 'self_serve',
    },
    bills_with_payer_live_invoice: {
      what: "This client's work is on the other person's unsent invoice",
      workSafe: 'so nothing changed',
      action: 'Send or discard that invoice first, then change the shared billing',
      severity: 'self_serve',
    },
  },
  page_write: {
    availability_changed: {
      what: 'Someone else changed this availability while you had it open',
      workSafe: 'so nothing was saved',
      action: 'Close this and open it again to see their change',
      severity: 'self_serve',
    },
    availability_changed_board: {
      what: 'Someone else changed this availability just now',
      workSafe: 'so the board has been reloaded',
      action: 'Check it, then make your change again',
      severity: 'self_serve',
    },
    server: {
      what: "That didn't save",
      workSafe: 'and nothing was changed',
      action: 'Try once more, and if it happens again, tell {contact_system}{ref}',
      severity: 'needs_admin',
    },
    busy: {
      what: 'Things are busy for a moment',
      workSafe: 'so nothing was changed',
      action: 'Try again in a minute',
      severity: 'retriable',
    },
    conflict: {
      what: 'Someone else changed this while you had it open',
      workSafe: "so yours didn't save",
      action: 'Reload the page, then make your change again',
      severity: 'self_serve',
    },
    rejected: {
      what: 'Nothing was saved yet: a box that needs an answer is blank, or a date or number is out of range',
      action: 'Fix that box, then save again',
      severity: 'self_serve',
    },
    gone: {
      what: "That isn't here any more",
      workSafe: 'so nothing was changed',
      action: 'Reload the page to see the current list',
      severity: 'self_serve',
    },
    offline: {
      what: "You're offline",
      workSafe: 'so nothing was saved',
      action: 'Reconnect to the internet, then save again',
      severity: 'self_serve',
    },
    unreachable: {
      what: "The save can't get through right now",
      workSafe: 'so nothing was saved',
      action: "Try again in a minute, and if it's still down, tell {contact_system}",
      severity: 'needs_admin',
    },
    not_on_care_team: {
      what: "You're not on this client's care team",
      workSafe: 'so nothing was changed',
      action: 'Ask {contact_admin} to add you',
      severity: 'needs_admin',
    },
    forbidden: {
      what: "This isn't part of your role",
      workSafe: 'so nothing was changed',
      action: 'Ask {contact_admin} if it should be',
      severity: 'needs_admin',
    },
    session: {
      what: 'You were signed out',
      workSafe: 'so nothing was saved',
      action: 'Sign in again, then make your change',
      severity: 'self_serve',
    },
  },
  page_crash: {
    crashed: {
      what: 'This page hit a problem',
      workSafe: 'and everything you saved is safe',
      action: 'Reload the page, and if it happens again, tell {contact_system} the reference below',
      severity: 'needs_admin',
    },
  },
  not_found: {
    page: {
      what: "This page doesn't exist, maybe because the link is old or mistyped",
      action: 'Go to Home or Clients from the menu',
      severity: 'self_serve',
    },
  },
};

// ---------------------------------------------------------------------
// Lookup + formatting
// ---------------------------------------------------------------------

/** The key every mapper answers first for a write or read that met an
 *  expired sign-in where the page kept its work and is going to sign-in
 *  (src/lib/keptWork.ts), on every surface. */
export const SESSION_EXPIRED_KEY = 'session_expired';
/** The same, where the work was not kept: it is still on screen, and the
 *  person signs in again and tries once more. */
export const SESSION_EXPIRED_RETRY_KEY = 'session_expired_retry';

/** An expired sign-in never names the support contact: nothing is broken.
 *  "Your work is still here" shows only where it is true. */
const SESSION_EXPIRED: ErrorDescriptor = {
  what: 'You were signed out',
  workSafe: 'but your work is still here',
  action: 'Sign in again, then save it',
  severity: 'self_serve',
};
const SESSION_EXPIRED_RETRY: ErrorDescriptor = {
  what: 'You were signed out',
  workSafe: "so that didn't save",
  action: 'Sign in again, then do it once more',
  severity: 'self_serve',
};

/** The expired sign-in's key for this failure: kept or not. */
function sessionKeyOf(e: unknown): string {
  return authExpiredWorkKept(e) ? SESSION_EXPIRED_KEY : SESSION_EXPIRED_RETRY_KEY;
}

/** Registry lookup; an unknown or absent key gets the honest generic. */
export function errorDescriptor(
  surface: ErrorSurface,
  key: string | null | undefined,
): ErrorDescriptor {
  if (key === SESSION_EXPIRED_KEY) return SESSION_EXPIRED;
  if (key === SESSION_EXPIRED_RETRY_KEY) return SESSION_EXPIRED_RETRY;
  if (key == null) return UNKNOWN_FAILURE;
  return ERROR_COPY[surface][key] ?? UNKNOWN_FAILURE;
}

function substitute(
  text: string,
  contact: SupportContact,
  vars: Record<string, string> | undefined,
): string {
  let out = text.replace(/\{contact_(system|billing|admin)\}/g, (_m, category: ContactCategory) =>
    contactDisplay(contactFor(contact, category)),
  );
  out = out.replace(/\{contact\}/g, contactDisplay(contact));
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      out = out.replace(new RegExp(`\\{${name}\\}`, 'g'), value);
    }
  }
  return out;
}

/** A screen's own sentence with its contacts filled in: `{contact_system}`,
 *  `{contact_billing}` and `{contact_admin}` (and `{contact}`, the single
 *  support contact). */
export function contactSentence(text: string, contact: SupportContact): string {
  return substitute(text, contact, undefined);
}

export interface FourAnswers {
  what: string;
  workSafe: string | null;
  action: string;
  /** Answer 4 as shown: the escalation line, or (needs_admin) the action
   *  itself since the action IS the ask; null when the action is the fix. */
  who: string | null;
}

/** The four answers, contact-resolved and var-substituted. */
export function fourAnswersOf(
  descriptor: ErrorDescriptor,
  contact: SupportContact,
  vars?: Record<string, string>,
): FourAnswers {
  const action = substitute(descriptor.action, contact, vars);
  const escalation = escalationOf(descriptor.severity, contact);
  return {
    what: substitute(descriptor.what, contact, vars),
    workSafe: descriptor.workSafe === undefined ? null : substitute(descriptor.workSafe, contact, vars),
    action,
    who:
      descriptor.severity === 'needs_admin' || descriptor.severity === 'needs_platform'
        ? action
        : escalation === null
          ? null
          : `${escalation}${vars?.ref ?? ''}`,
  };
}

/** The four answers joined into one flowing message, for the inline
 *  role="alert" slots that render a plain string (composer status line,
 *  feed rows, RefinePanel). <ErrorNotice> is the banner edition. */
export function errorText(
  surface: ErrorSurface,
  key: string | null | undefined,
  contact: SupportContact,
  vars?: Record<string, string>,
): string {
  const descriptor = errorDescriptor(surface, key);
  return joinedAnswers(fourAnswersOf(descriptor, contact, vars), descriptor.oneSentence === true);
}

/** Two sentences: what happened and whether the work is safe, then what
 *  to do and (retriable) who to tell if it happens again. A oneSentence
 *  entry joins the two with a comma. */
function joinedAnswers(a: FourAnswers, oneSentence = false): string {
  const first = a.workSafe === null ? a.what : `${a.what}, ${a.workSafe}`;
  // needs_admin's who IS the action; don't say it twice.
  const second = a.who !== null && a.who !== a.action ? `${a.action}${a.who}` : a.action;
  const text = oneSentence ? endSentence(`${first}, ${second}`) : `${endSentence(first)} ${endSentence(second)}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ---------------------------------------------------------------------
// Wire-shape -> registry-key mappers (the per-form if/else chains,
// written once). Keys, not copy: the surface picks the words.
// ---------------------------------------------------------------------

/** The body's `error` code on an ApiError; null otherwise. */
export function errorCodeOf(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  if (e.body && typeof e.body === 'object') {
    const code = (e.body as { error?: unknown }).error;
    if (typeof code === 'string') return code;
  }
  return null;
}

/**
 * Payroll Hours' refusal when the tenant's config cannot say what a
 * per-visit service pays: its own sentence, naming the band by the
 * label the page reads from the tenant's own bands. Null for any other
 * failure, which the page shows as a read failure.
 */
export function perVisitPayText(
  e: unknown,
  labelOf: (band: string) => string,
  contact: SupportContact,
): string | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const code = errorCodeOf(e);
  const band = (e.body as { band?: unknown } | null)?.band;
  if (typeof band !== 'string') return null;
  const label = labelOf(band);
  if (code === 'per_visit_pay_unconfigured') {
    return `Payroll can't be made yet because ${label} is paid per visit and has no visit pay set. Ask ${contactFor(contact, 'billing').name} to set it.`;
  }
  if (code === 'per_visit_pay_misconfigured') {
    return `Payroll can't be made yet because the visit pay for ${label} is set on the wrong kind of service. Ask ${contactFor(contact, 'billing').name} to fix it.`;
  }
  return null;
}

/** The body's `field` on an ApiError; null otherwise. The only reader of
 *  it: a field name reaches a screen only through a label source. */
export function fieldOf(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  if (e.body && typeof e.body === 'object') {
    const field = (e.body as { field?: unknown }).field;
    if (typeof field === 'string') return field;
  }
  return null;
}

function bodyErrorOf(e: ApiError): string | undefined {
  return errorCodeOf(e) ?? undefined;
}

/** True for the AI tools' refusal for length: a 400 text_too_long. */
export function isTextTooLongRefusal(e: unknown): boolean {
  return e instanceof ApiError && e.status === 400 && bodyErrorOf(e) === 'text_too_long';
}

/** The ceiling the server refused at, from a 400 text_too_long body's
 *  `max_chars`, for the text_too_long sentence's {max_chars} var; null
 *  for any other failure or for anything short of a positive whole
 *  number, and the surface then says its own ceiling. The browser's
 *  reading can be ten minutes old (the tenant-config cache); the
 *  server's is the one that refused. */
export function serverMaxCharsOf(e: unknown): number | null {
  if (!isTextTooLongRefusal(e)) return null;
  const body = (e as ApiError).body;
  if (!body || typeof body !== 'object') return null;
  const v = (body as { max_chars?: unknown }).max_chars;
  return typeof v === 'number' && Number.isSafeInteger(v) && v > 0 ? v : null;
}

/** The three reasons a model call answers 429, told apart by the body's
 *  error code: the per-person allowance and the organization's daily
 *  budget are not "wait a minute". */
function throttleKeyOf(e: ApiError): string {
  const code = bodyErrorOf(e);
  if (code === 'cm_cap_exceeded') return 'cap_reached';
  if (code === 'tenant_token_budget_exceeded') return 'tenant_budget';
  return 'rate_limited';
}

/** Generate-call failures for the three drafter forms (429/403/400/413
 *  + the 502 ndjson terminal codes). Unknown shapes -> null -> generic. */
export function aiGenerateKeyOf(e: unknown): string | null {
  if (isAuthExpiredError(e)) return sessionKeyOf(e);
  if (!(e instanceof ApiError)) return null;
  if (e.status === 429) return throttleKeyOf(e);
  if (e.status === 403) return 'no_access';
  // A text past the input ceiling has its own sentence; any other 400 is
  // still a field missing or invalid.
  if (e.status === 400) return bodyErrorOf(e) === 'text_too_long' ? 'text_too_long' : 'invalid_input';
  if (e.status === 413) return 'too_large';
  if (e.status === 502) {
    const code = bodyErrorOf(e);
    if (code === 'anthropic_max_tokens') return 'max_tokens';
    if (code === 'anthropic_invalid_json') return 'invalid_shape';
    return 'service_down';
  }
  return null;
}

/** The PDF codes the medication list's PDF door is refused with, before
 *  any model call; the browser checks the first two itself. */
export const MED_LIST_PDF_REFUSALS = ['pdf_too_large', 'pdf_not_a_pdf', 'pdf_too_many_pages'] as const;

/** A PDF read's failure: a refusal the server named, else as any generate
 *  call; never null, so every failure shows a notice. */
export function medListPdfKeyOf(e: unknown): string {
  if (isAuthExpiredError(e)) return sessionKeyOf(e);
  if (e instanceof ApiError && e.status === 400) {
    const code = bodyErrorOf(e);
    if (code !== undefined && (MED_LIST_PDF_REFUSALS as readonly string[]).includes(code)) return code;
  }
  if (e instanceof ApiError) return aiGenerateKeyOf(e) ?? 'unknown';
  return 'service_down';
}

/** A photo read's failure on either photo door, never null so every
 *  failure shows a notice: a file that could not be read is the photo
 *  fix; a server answer maps like any generate call, an unmapped status
 *  reading the honest generic; anything else (a fetch that never got an
 *  answer) is the service not answering. */
export function photoIntakeKeyOf(e: unknown): string {
  if (isAuthExpiredError(e)) return sessionKeyOf(e);
  if (e instanceof PhotoUnreadableError) return 'invalid_input';
  if (e instanceof ApiError) return aiGenerateKeyOf(e) ?? 'unknown';
  return 'service_down';
}

/** Second-round-edit (refine / gap-interview) failures. A text_too_long
 *  reaches the screen through src/lib/aiTextTooLong.ts, which names the
 *  box; this key is the family, for every other caller. */
export function aiRefineKeyOf(e: unknown): string {
  if (isAuthExpiredError(e)) return sessionKeyOf(e);
  if (e instanceof ApiError && e.status === 429) return throttleKeyOf(e);
  if (e instanceof ApiError && e.status === 403) return 'no_access';
  if (e instanceof ApiError && e.status === 400 && bodyErrorOf(e) === 'text_too_long') return 'text_too_long';
  return 'failed';
}

/** The composer's post-then-read beat: a read refused at the person's
 *  allowance or the organization's daily budget has its own line;
 *  every other failure is the plain "only the reading failed". */
export function noteReadKeyOf(e: unknown): string {
  if (isAuthExpiredError(e)) return sessionKeyOf(e);
  if (e instanceof ApiError && e.status === 429) {
    const code = bodyErrorOf(e);
    if (code === 'cm_read_cap_exceeded') return 'read_cap';
    if (code === 'tenant_token_budget_exceeded') return 'tenant_budget';
  }
  return 'read_failed';
}

export interface KeyedError {
  key: string | null;
  vars?: Record<string, string>;
}

/** Save-to-drafts failures (shared by the three tool pages). The cap's
 *  limit rides as a var so the copy can say the real number. */
export function aiSaveKeyOf(e: unknown): KeyedError {
  if (isAuthExpiredError(e)) return { key: sessionKeyOf(e) };
  if (!(e instanceof ApiError)) return { key: null };
  if (e.status === 409) {
    if (bodyErrorOf(e) === 'draft_cap_exceeded') {
      const limit = (e.body as { limit?: number }).limit ?? 25;
      return { key: 'cap_reached', vars: { limit: String(limit) } };
    }
    return { key: 'conflict' };
  }
  if (e.status === 403) return { key: 'no_access' };
  if (e.status === 400) return { key: 'invalid_payload' };
  return { key: null };
}

/** File-to-record failures (the assessment drafter's fileMut). */
export function assessmentFileKeyOf(e: unknown): KeyedError {
  if (isAuthExpiredError(e)) return { key: sessionKeyOf(e) };
  if (e instanceof ApiError && e.status === 403) return { key: 'no_access' };
  if (e instanceof ApiError && e.status === 409) {
    if (bodyErrorOf(e) === 'draft_cap_exceeded') {
      const limit = (e.body as { limit?: number }).limit ?? 25;
      return { key: 'cap_reached', vars: { limit: String(limit) } };
    }
    return { key: 'conflict' };
  }
  return { key: 'failed' };
}

/** Mark-as-ready refusals (the 0133/0135 gate on the notes feed). */
export function markReadyKeyOf(e: unknown): string {
  if (isAuthExpiredError(e)) return sessionKeyOf(e);
  if (e instanceof ApiError && e.status === 409) {
    const error = bodyErrorOf(e);
    if (error === 'rate_not_on_file') {
      // A missing band is named as a band, a missing rate as a rate.
      const band = (e.body as { rate_band?: unknown } | null)?.rate_band;
      return band === null ? 'rate_band_missing' : 'rate_not_on_file';
    }
    if (error === 'hours_not_entered') return 'hours_not_entered';
    if (error === 'needs_hours') return 'needs_hours';
  }
  return 'failed';
}

// ---------------------------------------------------------------------
// The page surfaces' mappers and the reference a person can quote
// ---------------------------------------------------------------------

/** The status classes the page surfaces tell apart. `online` is the
 *  browser's own answer (navigator.onLine): offline is the person's
 *  connection, unreachable is Olive. */
function pageKeyOf(e: unknown, online: boolean, kind: 'read' | 'write'): string | null {
  if (isAuthExpiredError(e)) return sessionKeyOf(e);
  if (e instanceof TypeError) return online ? 'unreachable' : 'offline';
  if (!(e instanceof ApiError)) return null;
  if (e.status === 401) return 'session';
  if (e.status === 403) return bodyErrorOf(e) === 'not_on_care_team' ? 'not_on_care_team' : 'forbidden';
  if (e.status === 404) return kind === 'read' ? 'not_found' : 'gone';
  if (isDatabaseBusy(e)) return 'busy';
  if (e.status >= 500) return 'server';
  if (kind === 'write' && e.status === 409) return 'conflict';
  if (kind === 'write' && (e.status === 400 || e.status === 422 || e.status === 413)) return 'rejected';
  return null;
}

export function pageReadKeyOf(e: unknown, online: boolean = navigator.onLine): string | null {
  return pageKeyOf(e, online, 'read');
}

export function pageWriteKeyOf(e: unknown, online: boolean = navigator.onLine): string | null {
  return pageKeyOf(e, online, 'write');
}

/** The id the server put on the failure (its request id, or the
 *  invocation id an unhandled fault answers with); null when the failure
 *  never reached the server. */
export function referenceOf(e: unknown): string | null {
  if (!(e instanceof ApiError) || !e.body || typeof e.body !== 'object') return null;
  const body = e.body as { request_id?: unknown; invocation_id?: unknown };
  const id = body.request_id ?? body.invocation_id;
  if (typeof id === 'string' && id.length > 0) return id;
  if (typeof id === 'number') return String(id);
  return null;
}

/** The first eight characters of a reference, enough to find it. */
export function shortRef(ref: string): string {
  return ref.slice(0, 8);
}

/** The clause an action ends with when there is a reference to quote. */
export function refClause(ref: string | null): string {
  return ref === null ? '' : ` and give them reference ${shortRef(ref)}`;
}

// ---------------------------------------------------------------------
// The sentences a screen shows a failure through
// ---------------------------------------------------------------------

/** The session sentence for an expired sign-in, or null for any other
 *  failure: the first line of a screen's own error map, so its status
 *  branches never word an expired sign-in. */
export function sessionExpiredText(e: unknown): string | null {
  return isAuthExpiredError(e) ? errorText('page_write', sessionKeyOf(e), FALLBACK_SUPPORT_CONTACT) : null;
}

/** A failed save, as one sentence: page_write by status, with the
 *  reference clause. */
export function pageWriteText(e: unknown, contact: SupportContact): string {
  return errorText('page_write', pageWriteKeyOf(e), contact, { ref: refClause(referenceOf(e)) });
}

/** The billing household's codes (0215), the keys of its surface. */
export const BILLING_HOUSEHOLD_CODES = [
  'client_bills_with',
  'billing_household_changed',
  'billing_household_invalid',
  'bills_with_self',
  'bills_with_target_is_partner',
  'bills_with_client_is_payer',
  'bills_with_partner_live_invoice',
  'bills_with_payer_live_invoice',
] as const;

/** A billing household refusal as one sentence, or null when the failure
 *  is not one: the caller then shows its own. */
export function billingHouseholdText(e: unknown, contact: SupportContact): string | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const code = bodyErrorOf(e);
  if (code === undefined || !(BILLING_HOUSEHOLD_CODES as readonly string[]).includes(code)) return null;
  return errorText('billing_household', code, contact);
}

/** A save over a newer availability change (0216), as the dialog's or
 *  the board's sentence; null when the failure is not that refusal. */
export function availabilityChangedText(
  e: unknown,
  contact: SupportContact,
  where: 'dialog' | 'board',
): string | null {
  if (!(e instanceof ApiError) || e.status !== 409 || bodyErrorOf(e) !== 'availability_changed') return null;
  return errorText('page_write', where === 'dialog' ? 'availability_changed' : 'availability_changed_board', contact);
}

/** A failed load, as one sentence: page_read by status, naming the thing. */
export function pageReadText(e: unknown, contact: SupportContact, what: string): string {
  return errorText('page_read', pageReadKeyOf(e), contact, { what, ref: refClause(referenceOf(e)) });
}

/** A refusal a retry cannot fix (401, 403, 404), as pageWriteText; null
 *  otherwise. */
export function refusalText(e: unknown, contact: SupportContact): string | null {
  if (!(e instanceof ApiError)) return null;
  if (e.status !== 401 && e.status !== 403 && e.status !== 404) return null;
  return pageWriteText(e, contact);
}

/** The server codes whose `message` is authored for the screen. Never a
 *  validator code. */
export const SERVER_SENTENCE_CODES = {
  notes: [
    'note_not_own',
    'rate_band_not_allowed',
    'rate_band_off',
    'duplicate_spice_visit',
    'hours_not_on_increment',
    'note_not_eligible_for_billing',
    'hours_not_entered',
    'rate_not_on_file',
    'note_on_invoice',
    'note_date_crosses_native_start',
    'note_date_in_future',
    // LP-45: the note's pay period has gone to payroll.
    'note_pay_period_closed',
    // LP-45: a new note dated inside a closed pay period.
    'note_date_pay_period_closed',
    // LP-46: the long-distance tick only on a SPICE-family band with hours.
    'long_distance_not_spice',
  ],
  time_entries: ['pay_period_closed', 'hours_not_on_increment'],
  record_access: ['client_record_access_malformed'],
  // care_record_read_only: a seat that reads the record but does not keep
  // it (assertKeepsClinicalRecord, LP-48).
  record_edits: ['bill_to_not_allowed', 'prescriber_pick_unavailable', 'provider_pick_unavailable', 'care_record_read_only'],
  // LP-45: an edit of a Directory note someone else wrote.
  directory_notes: ['directory_note_not_own'],
  // The People doors' Directory Editor refusals: the sentence carries the
  // count of clients still assigned, or which switches to turn off first.
  people: ['directory_editor_assigned', 'directory_editor_care_flags'],
} as const;
export type ServerSentenceFamily = keyof typeof SERVER_SENTENCE_CODES;

/** The server's own sentence, only when the body's code is listed for
 *  this family: its non-empty `message`; null otherwise. Never reads
 *  `detail`. */
export function serverSentence(e: unknown, family: ServerSentenceFamily): string | null {
  const code = errorCodeOf(e);
  if (code === null) return null;
  const listed: readonly string[] = SERVER_SENTENCE_CODES[family];
  if (!listed.includes(code)) return null;
  const message = ((e as ApiError).body as { message?: unknown }).message;
  return typeof message === 'string' && message.trim().length > 0 ? message : null;
}

/**
 * A save screen's words for a failure that is not an answer it
 * recognises: the shared page_write text for a failure that never
 * reached the server, one that is not an ApiError, or a status of 500 or
 * above (offline, unreachable, busy, server, or the honest generic).
 * Null for every 4xx, so the screen falls through to its own messages,
 * whose causes only it can vouch for. `workSafe: false` drops the
 * work-safety line, for a save that can fail after part of it landed.
 */
export function saveFailureText(
  err: unknown,
  contact: SupportContact,
  opts: { workSafe?: boolean } = {},
): string | null {
  // An expired sign-in is not the screen's own answer: the shared sentence.
  if (isAuthExpiredError(err)) return errorText('page_write', sessionKeyOf(err), contact);
  if (err instanceof ApiError && err.status < 500) return null;
  const descriptor = errorDescriptor('page_write', pageWriteKeyOf(err));
  const shown: ErrorDescriptor =
    opts.workSafe === false
      ? { what: descriptor.what, action: descriptor.action, severity: descriptor.severity }
      : descriptor;
  return joinedAnswers(fourAnswersOf(shown, contact, { ref: refClause(referenceOf(err)) }));
}
